import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError, num, money } from "@/lib/api";
import { Card, PageHeader, Btn, Modal, Input, Select, Th, Td, EmptyState } from "@/components/admin/ui";

const TABS = ["Countries", "Regions", "Cities"];

export default function Locations() {
  const [tab, setTab] = useState("Countries");
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [modal, setModal] = useState(null);
  const [country, setCountry] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => {
    api.get("/countries").then((r) => setCountries(r.data.items));
    api.get("/regions").then((r) => setRegions(r.data.items));
    api.get("/cities").then((r) => setCities(r.data.items));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cName = (id) => countries.find((c) => c.id === id)?.name || "—";
  const rName = (id) => regions.find((r) => r.id === id)?.name || "—";

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (modal === "country") await api.post("/countries", { name: form.name, code: form.code, timezone_default: form.tz || "UTC" });
      if (modal === "region") await api.post("/regions", { country_id: form.country_id, name: form.name });
      if (modal === "city") await api.post("/cities", { region_id: form.region_id, country_id: regions.find((r) => r.id === form.region_id)?.country_id, name: form.name, timezone: form.tz || "UTC" });
      toast.success("Created"); setModal(null); setForm({}); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Locations" subtitle="Manage the geographic hierarchy: Countries → Regions → Cities" />
      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`loc-tab-${t}`} className={`px-4 py-2 rounded-lg text-sm font-semibold ${tab === t ? "bg-[#1D4ED8] text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}>{t}</button>
        ))}
        <div className="flex-1" />
        {tab === "Countries" && <Btn data-testid="add-country-btn" onClick={() => { setModal("country"); setForm({}); }}>+ Country</Btn>}
        {tab === "Regions" && <Btn data-testid="add-region-btn" onClick={() => { setModal("region"); setForm({}); }}>+ Region</Btn>}
        {tab === "Cities" && <Btn data-testid="add-city-btn" onClick={() => { setModal("city"); setForm({}); }}>+ City</Btn>}
      </div>

      <Card>
        {tab === "Countries" && (
          countries.length === 0 ? <EmptyState title="No countries yet" desc="Create a country to start building your global hierarchy." /> : (
            <table className="w-full"><thead className="border-b border-white/10"><tr><Th>Name</Th><Th>Code</Th><Th>Default TZ</Th></tr></thead>
              <tbody>{countries.map((c) => <tr key={c.id} onClick={() => api.get(`/dashboard/country/${c.id}`).then((r) => setCountry(r.data)).catch(() => {})} className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer" data-testid={`country-row-${c.name}`}><Td className="text-white font-medium">{c.name}</Td><Td>{c.code}</Td><Td className="text-slate-400">{c.timezone_default}</Td></tr>)}</tbody>
            </table>
          )
        )}
        {tab === "Regions" && (
          regions.length === 0 ? <EmptyState title="No regions yet" /> : (
            <table className="w-full"><thead className="border-b border-white/10"><tr><Th>Region</Th><Th>Country</Th></tr></thead>
              <tbody>{regions.map((r) => <tr key={r.id} className="border-b border-white/5" data-testid={`region-row-${r.name}`}><Td className="text-white font-medium">{r.name}</Td><Td className="text-slate-400">{cName(r.country_id)}</Td></tr>)}</tbody>
            </table>
          )
        )}
        {tab === "Cities" && (
          cities.length === 0 ? <EmptyState title="No cities yet" /> : (
            <table className="w-full"><thead className="border-b border-white/10"><tr><Th>City</Th><Th>Region</Th><Th>Country</Th><Th>Timezone</Th></tr></thead>
              <tbody>{cities.map((c) => <tr key={c.id} className="border-b border-white/5" data-testid={`city-row-${c.name}`}><Td className="text-white font-medium">{c.name}</Td><Td className="text-slate-400">{rName(c.region_id)}</Td><Td className="text-slate-400">{cName(c.country_id)}</Td><Td className="text-slate-400">{c.timezone}</Td></tr>)}</tbody>
            </table>
          )
        )}
      </Card>

      <Modal open={!!modal} onClose={() => setModal(null)} title={`Add ${modal || ""}`}>
        <form onSubmit={submit} className="space-y-3">
          {modal === "country" && (<>
            <Input label="Country name" data-testid="country-name-input" required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="India" />
            <Input label="Code" data-testid="country-code-input" required value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="IN" />
            <Input label="Default timezone" value={form.tz || ""} onChange={(e) => setForm({ ...form, tz: e.target.value })} placeholder="Asia/Kolkata" />
          </>)}
          {modal === "region" && (<>
            <Select label="Country" data-testid="region-country-select" required value={form.country_id || ""} onChange={(e) => setForm({ ...form, country_id: e.target.value })}>
              <option value="">Select country</option>{countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
            <Input label="Region name" data-testid="region-name-input" required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Tamil Nadu" />
          </>)}
          {modal === "city" && (<>
            <Select label="Region" data-testid="city-region-select" required value={form.region_id || ""} onChange={(e) => setForm({ ...form, region_id: e.target.value })}>
              <option value="">Select region</option>{regions.map((r) => <option key={r.id} value={r.id}>{r.name} · {cName(r.country_id)}</option>)}
            </Select>
            <Input label="City name" data-testid="city-name-input" required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Chennai" />
            <Input label="Timezone" value={form.tz || ""} onChange={(e) => setForm({ ...form, tz: e.target.value })} placeholder="Asia/Kolkata" />
          </>)}
          <Btn data-testid="location-submit" className="w-full">Create</Btn>
        </form>
      </Modal>

      <Modal open={!!country} onClose={() => setCountry(null)} title={country ? `${country.country.name} · Country Operations` : ""} wide>
        {country && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Workers</div><div className="font-mono-metric text-xl font-bold text-white mt-1">{num(country.worker_count)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Cities</div><div className="font-mono-metric text-xl font-bold text-white mt-1">{num(country.city_count)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Leads</div><div className="font-mono-metric text-xl font-bold text-[#1D4ED8] mt-1">{num(country.metrics.leads)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Revenue</div><div className="font-mono-metric text-xl font-bold text-emerald-400 mt-1">{money(country.metrics.revenue)}</div></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Tasks</div><div className="font-mono-metric text-lg font-bold text-white mt-1">{num(country.metrics.tasks_assigned)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Completion</div><div className="font-mono-metric text-lg font-bold text-emerald-400 mt-1">{country.metrics.completion_rate}%</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Meetings</div><div className="font-mono-metric text-lg font-bold text-white mt-1">{num(country.metrics.meetings)}</div></div>
              <div className="p-4 rounded-lg bg-[#0B0F19] border border-white/10"><div className="text-xs text-slate-500 uppercase">Sales</div><div className="font-mono-metric text-lg font-bold text-[#1D4ED8] mt-1">{num(country.metrics.sales)}</div></div>
            </div>
            <div>
              <h4 className="font-head font-semibold text-white mb-2">Cities (Country → City → Team → Worker)</h4>
              <div className="space-y-2 max-h-56 overflow-y-auto cc-scroll">
                {country.cities.map((c) => <div key={c.id} className="flex justify-between px-4 py-2.5 rounded-lg bg-[#0B0F19] border border-white/10"><span className="text-slate-200 text-sm">{c.name}</span><span className="text-slate-500 text-xs">{c.timezone}</span></div>)}
                {country.cities.length === 0 && <p className="text-slate-500 text-sm">No cities in this country yet.</p>}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
